import { getSupabaseClient, getAuthenticatedSupabaseClient } from './supabaseClerk';
import { API_URL } from '../config/apiConfig';

/**
 * User data structure matching Clerk + Supabase schema
 */
export interface User {
  id: string;
  clerk_user_id: string;
  email: string;
  full_name?: string;
  subscription_tier?: string;
  subscription_status?: string;
  stripe_customer_id?: string;
  credits: number;
  created_at: string;
  updated_at: string;
}

/**
 * Get or create user in Supabase from Clerk user data
 * This function is called after Clerk authentication to ensure user exists in our DB
 */
export async function getOrCreateUser(
  clerkUserId: string,
  email: string,
  fullName?: string,
  clerkToken?: string
): Promise<User> {
  const supabase = getSupabaseClient(clerkToken);

  try {
    // Try to find existing user
    const { data: existingUser, error: fetchError } = await supabase
      .from('users')
      .select('*')
      .eq('clerk_user_id', clerkUserId)
      .single();

    if (existingUser && !fetchError) {
      // Update user info if changed
      const updates: Partial<User> = {};
      if (email !== existingUser.email) updates.email = email;
      if (fullName && fullName !== existingUser.full_name) updates.full_name = fullName;

      if (Object.keys(updates).length > 0) {
        const { data: updated } = await supabase
          .from('users')
          .update(updates)
          .eq('clerk_user_id', clerkUserId)
          .select()
          .single();
        
        return updated || existingUser;
      }

      return existingUser;
    }

    // User doesn't exist, create new user with signup bonus
    const { data: newUser, error: createError } = await supabase
      .from('users')
      .insert({
        clerk_user_id: clerkUserId,
        email,
        full_name: fullName,
        subscription_tier: 'free',
        subscription_status: 'inactive',
        credits: 0, // Credits will be granted by backend via API
      })
      .select()
      .single();

    if (createError) {
      console.error('[Supabase] Error creating user:', createError);
      throw createError;
    }

    // Call backend to grant signup bonus (100 credits)
    try {
      if (clerkToken) {
        console.log('[UserService] Calling signup bonus API:', `${API_URL}/api/users/grant-signup-bonus`);
        const response = await fetch(`${API_URL}/api/users/grant-signup-bonus`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${clerkToken}`,
          },
          body: JSON.stringify({
            clerk_user_id: clerkUserId,
          }),
        });
        
        if (response.ok) {
          console.log('[UserService] Signup bonus granted successfully');
        } else {
          console.warn('[UserService] Signup bonus API returned error:', response.status);
        }
      }
    } catch (bonusError) {
      console.warn('[Supabase] Failed to grant signup bonus:', bonusError);
      // Don't fail user creation if bonus grant fails
    }

    return newUser;
  } catch (error) {
    console.error('[Supabase] Error in getOrCreateUser:', error);
    throw error;
  }
}

/**
 * Get user by Clerk user ID
 */
export async function getUserByClerkId(
  clerkUserId: string,
  clerkToken?: string
): Promise<User | null> {
  const supabase = getSupabaseClient(clerkToken);

  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('clerk_user_id', clerkUserId)
    .single();

  if (error) {
    console.error('[Supabase] Error fetching user:', error);
    return null;
  }

  return data;
}

/**
 * Update user subscription info (called by backend webhook)
 */
export async function updateUserSubscription(
  clerkUserId: string,
  subscriptionData: {
    subscription_tier?: string;
    subscription_status?: string;
    stripe_customer_id?: string;
    stripe_subscription_id?: string;
  },
  clerkToken?: string
): Promise<User | null> {
  const supabase = getSupabaseClient(clerkToken);

  const { data, error } = await supabase
    .from('users')
    .update(subscriptionData)
    .eq('clerk_user_id', clerkUserId)
    .select()
    .single();

  if (error) {
    console.error('[Supabase] Error updating subscription:', error);
    return null;
  }

  return data;
}
